// Monthly Attendance Grid — a real attendance matrix (employees × days).
// Part of the HR module for Khyati Portal / RSR International.
// Source feed: OnTime / Secureye biometric device, via the factory-PC bridge.
//
// A quarantined date (mig 182) renders BLANK by design — the feed refused an all-zero roster
// rather than fabricate a factory-wide absence. Blank is honest but indistinguishable from a
// holiday or a day nobody worked, which is exactly how the 5–9 August outage stayed invisible for
// six days. So a refused day is now marked explicitly in the header, the cells and the legend.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { HRC, fmtDate, Card, SectionTitle, Pill, Btn, Field, input, MonthPicker, Modal, Empty, Loading } from './hrkit';
import { ListFooter } from '../../ui';

const fmtMonth = (m) => {
  if (!m) return '';
  const [y, mo] = String(m).split('-');
  const d = new Date(Number(y), Number(mo) - 1, 1);
  return isNaN(d) ? m : d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};

// Status → marker glyph + colour. Markers stay short so cells stay ~26px.
const MARK = {
  present:  { glyph: 'P',  color: HRC.green,  label: 'Present' },
  absent:   { glyph: 'A',  color: HRC.red,    label: 'Absent' },
  half_day: { glyph: '½',  color: HRC.amber,  label: 'Half day' },
  leave:    { glyph: 'L',  color: HRC.navy,   label: 'Leave' },
  week_off: { glyph: 'WO', color: HRC.muted,  label: 'Week off' },
  holiday:  { glyph: 'H',  color: '#8B5CF6',  label: 'Holiday' },
};

// Build a list of day numbers + which are Sundays (week-off hint) for the month.
const buildDays = (month, days) => {
  const n = Math.max(0, Number(days) || 0);
  const [y, mo] = String(month || '').split('-').map(Number);
  const out = [];
  for (let d = 1; d <= n; d++) {
    const dt = (y && mo) ? new Date(y, mo - 1, d) : null;
    out.push({ day: d, sunday: dt ? dt.getDay() === 0 : false });
  }
  return out;
};

const tooltipFor = (day, c) => {
  if (!c) return `Day ${day} · —`;
  const parts = [`Day ${day}`, (MARK[c.status]?.label || c.status || '—')];
  if (c.late > 0) parts.push(`late ${c.late}m`);
  if (c.early > 0) parts.push(`early ${c.early}m`);
  if (c.miss) parts.push('miss-punch');
  if (c.ot > 0) parts.push(`OT ${c.ot}h`);
  if (c.units > 0) parts.push(`${c.units} units`);
  return parts.join(' · ');
};

// Legend swatch.
const Legend = () => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center', fontSize: 11, color: HRC.muted }}>
    {Object.keys(MARK).map((k) => {
      const m = MARK[k];
      return (
        <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 20, height: 20, borderRadius: 5, fontSize: 11, fontWeight: 800,
            color: m.color, background: m.color + '22' }}>{m.glyph}</span>
          {m.label}
        </span>
      );
    })}
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span style={{ color: HRC.amber, fontWeight: 800 }}>•</span> late
    </span>
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span style={{ color: HRC.red, fontWeight: 800 }}>⊘</span> miss-punch
    </span>
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 20, height: 20, borderRadius: 5, fontSize: 11, fontWeight: 800, color: '#B4690E',
        background: 'repeating-linear-gradient(45deg,#FDF3E7,#FDF3E7 3px,#F7E3CA 3px,#F7E3CA 6px)' }}>⚠</span>
      source data was empty — not written
    </span>
  </div>
);

// ── Feed health banner ───────────────────────────────────────────────────────
// The grid is where a human finally noticed the 5–9 August outage, on the 11th. It should have
// been the thing that told them. Two independent conditions, both worth shouting about:
// the feed has gone SILENT (no successful import inside the threshold), and specific dates were
// REFUSED. Silence is the more dangerous one — it has no other signal anywhere in the system.
const FeedBanner = ({ health, quarantined, goTo }) => {
  const open = (quarantined || []).filter((q) => !q.resolved);
  if (!health && !open.length) return null;
  const stale = health && health.is_stale;
  if (!stale && !open.length) return null;

  const box = (tone, children, key) => {
    const c = tone === 'red' ? { bg: '#FDECEC', bd: '#F3C0C0', fg: '#A32020' }
                             : { bg: '#FDF3E7', bd: '#EFD2AC', fg: '#8A5206' };
    return (
      <div key={key} style={{ background: c.bg, border: `1px solid ${c.bd}`, color: c.fg,
        borderRadius: 8, padding: '10px 14px', fontSize: 12.5, marginBottom: 10, lineHeight: 1.5 }}>
        {children}
      </div>
    );
  };

  const out = [];
  if (stale) {
    out.push(box('red', (
      <>
        <strong>🔴 The attendance feed has gone silent.</strong>{' '}
        {health.hours_since_success == null
          ? 'No successful import has ever been recorded.'
          : `The last successful import was ${health.hours_since_success} hours ago (threshold ${health.stale_threshold_hours}h).`}{' '}
        The OnTime bridge runs twice daily, so this means a run did not happen or did not reach the
        server — check the factory PC. Days missed this way fall out of the bridge's 3-day window
        permanently.{' '}
        {goTo && <button onClick={() => goTo('feed')} style={linkBtn}>Open Feed &amp; Roster →</button>}
      </>
    ), 'stale'));
  }
  if (open.length) {
    out.push(box('amber', (
      <>
        <strong>⚠ {open.length} date{open.length > 1 ? 's' : ''} in this month {open.length > 1 ? 'were' : 'was'} refused, not recorded.</strong>{' '}
        The device sent a whole roster of zeros for{' '}
        <strong>{open.map((q) => q.date).join(', ')}</strong>, so {open.length > 1 ? 'they were' : 'it was'} left
        blank rather than marked absent — payroll and appraisals are not corrupted. Verify whether
        the factory was closed or the bridge missed {open.length > 1 ? 'these days' : 'this day'}.{' '}
        {goTo && <button onClick={() => goTo('feed')} style={linkBtn}>Request a backfill →</button>}
      </>
    ), 'quar'));
  }
  return <>{out}</>;
};

const linkBtn = {
  background: 'none', border: 'none', padding: 0, font: 'inherit', fontWeight: 700,
  color: 'inherit', textDecoration: 'underline', cursor: 'pointer',
};

// A single day cell. `quarantine` is the feed-event row for this date, when the feed refused it.
const DayCell = ({ day, cell, sunday, quarantine, onClick }) => {
  const m = cell ? MARK[cell.status] : null;
  // A refused day is hatched, not blank. Without this it is pixel-identical to a holiday.
  if (!cell && quarantine) {
    return (
      <td
        title={`Day ${day} · NOT WRITTEN — the OnTime feed sent an all-zero roster for this date, `
             + `so it was refused rather than recorded as absent.${quarantine.resolved ? ' (since recovered)' : ''}`}
        style={{
          width: 26, minWidth: 26, maxWidth: 26, height: 26, padding: 0, textAlign: 'center',
          borderRight: '1px solid #F1F0EE', borderBottom: '1px solid #F1F0EE',
          background: 'repeating-linear-gradient(45deg,#FDF3E7,#FDF3E7 3px,#F7E3CA 3px,#F7E3CA 6px)',
        }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: '#B4690E', lineHeight: '26px' }}>⚠</span>
      </td>
    );
  }
  const bg = sunday ? '#FAFAFC' : '#fff';
  return (
    <td
      title={cell ? tooltipFor(day, cell) : undefined}
      onClick={cell ? onClick : undefined}
      style={{
        width: 26, minWidth: 26, maxWidth: 26, height: 26, padding: 0, textAlign: 'center',
        borderRight: '1px solid #F1F0EE', borderBottom: '1px solid #F1F0EE',
        background: m ? m.color + '14' : bg, position: 'relative',
        cursor: cell ? 'pointer' : 'default',
      }}>
      {m && (
        <span style={{ fontSize: m.glyph.length > 1 ? 9 : 12, fontWeight: 800, color: m.color, lineHeight: '26px' }}>
          {m.glyph}
        </span>
      )}
      {cell && cell.late > 0 && (
        <span style={{ position: 'absolute', top: 1, right: 2, fontSize: 9, color: HRC.amber, fontWeight: 800, lineHeight: 1 }}>•</span>
      )}
      {cell && cell.miss && (
        <span style={{ position: 'absolute', bottom: 0, left: 2, fontSize: 8, color: HRC.red, fontWeight: 800, lineHeight: 1 }}>⊘</span>
      )}
    </td>
  );
};

const stickyTh = {
  position: 'sticky', top: 0, zIndex: 3, background: HRC.violet,
  fontSize: 10, color: HRC.navy, fontWeight: 800, textTransform: 'uppercase',
  letterSpacing: 0.4, padding: '8px 10px', whiteSpace: 'nowrap', textAlign: 'left',
  borderBottom: `1px solid ${HRC.border}`,
};
const stickyTd = {
  position: 'sticky', background: '#fff', fontSize: 12, color: HRC.text,
  padding: '4px 10px', whiteSpace: 'nowrap', borderBottom: '1px solid #F1F0EE',
  borderRight: `1px solid ${HRC.border}`,
};

export default function AttendanceGrid(props) {
  const { month, setMonth, goTo } = props || {};
  const [data, setData] = useState({ month: '', days: 0, employees: [], quarantined: [] });
  const [health, setHealth] = useState(null);
  const [depts, setDepts] = useState([]);
  const [dept, setDept] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [importing, setImporting] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [log, setLog] = useState(null);
  const [logLoading, setLogLoading] = useState(false);
  const [detail, setDetail] = useState(null); // { emp, day, cell }

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    setErr('');
    api.get('/hr/attendance/grid', { params: { month: month || undefined, dept: dept || undefined } })
      .then((r) => { if (alive) setData({ month: '', days: 0, employees: [], quarantined: [], ...(r.data?.data || {}) }); })
      .catch((e) => {
        if (!alive) return;
        setErr(e.response?.data?.message || 'Could not load attendance grid');
        setData({ month: '', days: 0, employees: [], quarantined: [] });
      })
      .finally(() => { if (alive) setLoading(false); });
    // Feed health is deliberately independent of the grid query: it must still render when the
    // grid is empty, because "no rows at all" is itself a symptom worth explaining.
    api.get('/hr/attendance/feed-health')
      .then((r) => { if (alive) setHealth(r.data?.data || null); })
      .catch(() => { if (alive) setHealth(null); });
    return () => { alive = false; };
  }, [month, dept]);

  useEffect(() => load(), [load]);

  useEffect(() => {
    let alive = true;
    api.get('/hr/departments')
      .then((r) => { if (alive) setDepts(r.data?.data || []); })
      .catch(() => { if (alive) setDepts([]); });
    return () => { alive = false; };
  }, []);

  const runImport = () => {
    setImporting(true);
    setNotice('');
    setErr('');
    api.post('/hr/attendance/import', { month: month || undefined })
      .then((r) => {
        const d = r.data?.data || {};
        setNotice(d.message || `Imported ${Number(d.rowsImported || 0)} rows${d.rowsSkipped ? `, skipped ${d.rowsSkipped}` : ''} from OnTime.`);
        load();
      })
      .catch((e) => setErr(e.response?.data?.message || 'Import failed'))
      .finally(() => setImporting(false));
  };

  const openLog = () => {
    setShowLog(true);
    setLogLoading(true);
    api.get('/hr/attendance/import-log')
      .then((r) => setLog(r.data?.data || []))
      .catch(() => setLog([]))
      .finally(() => setLogLoading(false));
  };

  const days = buildDays(data.month || month, data.days);
  const sundaySet = new Set(days.filter((d) => d.sunday).map((d) => d.day));
  const employees = data.employees || [];
  const quarantined = data.quarantined || [];
  const quarByDay = new Map(quarantined.map((q) => [q.day, q]));

  // Group rows by department for subheaders (data is already ordered by dept).
  const groups = [];
  let cur = null;
  employees.forEach((emp) => {
    const dn = emp.department_name || 'Unassigned';
    if (!cur || cur.name !== dn) { cur = { name: dn, rows: [] }; groups.push(cur); }
    cur.rows.push(emp);
  });

  // Summary columns appended after the day columns.
  const SUM_COLS = [
    { key: 'present', label: 'P', color: HRC.green },
    { key: 'absent', label: 'A', color: HRC.red },
    { key: 'leave', label: 'L', color: HRC.navy },
    { key: 'ot_hours', label: 'OT', color: HRC.amber },
    { key: 'units', label: 'Units', color: '#8B5CF6' },
  ];

  const totalDayCols = days.length;

  return (
    <div>
      <SectionTitle right={<span style={{ fontSize: 12, color: HRC.muted }}>{fmtMonth(data.month || month)}</span>}>
        Monthly Attendance Grid
      </SectionTitle>

      {/* Toolbar */}
      <Card style={{ padding: 14, marginBottom: 14 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
            <Field label="Month">
              <MonthPicker value={month} onChange={setMonth} />
            </Field>
            <Field label="Department">
              <select value={dept} onChange={(e) => setDept(e.target.value)} style={{ ...input, width: 'auto', minWidth: 170, padding: '7px 10px' }}>
                <option value="">All departments</option>
                {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Btn variant="ghost" size="sm" onClick={openLog}>Import history</Btn>
            <Btn variant="primary" onClick={runImport} disabled={importing} title="Attendance syncs automatically from the OnTime device; this refreshes the grid.">
              {importing ? 'Refreshing…' : '⟲ Refresh from OnTime'}
            </Btn>
          </div>
        </div>
        <div style={{ marginTop: 10, fontSize: 11, color: HRC.muted }}>
          Source: OnTime / Secureye biometric device
        </div>
      </Card>

      <FeedBanner health={health} quarantined={quarantined} goTo={goTo} />

      {notice && (
        <div style={{ background: HRC.green + '12', color: HRC.green, border: `1px solid ${HRC.green}33`,
          borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{notice}</div>
      )}
      {err && (
        <div style={{ background: HRC.red + '12', color: HRC.red, border: `1px solid ${HRC.red}33`,
          borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{err}</div>
      )}

      <Card style={{ padding: 14 }}>
        <div style={{ marginBottom: 12 }}><Legend /></div>

        {loading ? (
          <Loading />
        ) : !employees.length ? (
          <Empty>No attendance for this month. Try “Import from OnTime”.</Empty>
        ) : (
          <>
          <div style={{ overflowX: 'auto', border: `1px solid ${HRC.border}`, borderRadius: 8 }}>
            <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: 'max-content', minWidth: '100%' }}>
              <thead>
                <tr>
                  <th style={{ ...stickyTh, left: 0, zIndex: 4, minWidth: 72 }}>Code</th>
                  <th style={{ ...stickyTh, left: 72, zIndex: 4, minWidth: 160 }}>Name</th>
                  <th style={{ ...stickyTh, left: 232, zIndex: 4, minWidth: 130, borderRight: `1px solid ${HRC.border}` }}>Dept</th>
                  {days.map((d) => {
                    const q = quarByDay.get(d.day);
                    return (
                      <th key={d.day}
                        title={q ? `${q.date} — source data was empty, not written${q.resolved ? ' (since recovered)' : ''}`
                                 : (d.sunday ? 'Sunday' : undefined)}
                        style={{ ...stickyTh, position: 'sticky', top: 0, width: 26, minWidth: 26, padding: '8px 0',
                          textAlign: 'center',
                          background: q && !q.resolved ? '#F7E3CA' : d.sunday ? '#E6E5F2' : HRC.violet,
                          color: q && !q.resolved ? '#8A5206' : d.sunday ? HRC.muted : HRC.navy,
                          borderRight: '1px solid #F1F0EE' }}>
                        {d.day}
                      </th>
                    );
                  })}
                  {SUM_COLS.map((s) => (
                    <th key={s.key} style={{ ...stickyTh, width: 40, minWidth: 40, textAlign: 'center', color: s.color }}>{s.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <React.Fragment key={g.name}>
                    <tr>
                      <td colSpan={3 + totalDayCols + SUM_COLS.length}
                        style={{ position: 'sticky', left: 0, background: '#FAFAFC', fontSize: 10, fontWeight: 800,
                          color: HRC.navy, textTransform: 'uppercase', letterSpacing: 0.5, padding: '6px 10px',
                          borderBottom: '1px solid #F1F0EE' }}>
                        {g.name} · {g.rows.length}
                      </td>
                    </tr>
                    {g.rows.map((emp) => {
                      const sm = emp.summary || {};
                      const isPiece = emp.employee_type === 'piece_rate';
                      return (
                        <tr key={emp.id}>
                          <td style={{ ...stickyTd, left: 0, zIndex: 2, fontWeight: 700, color: HRC.navy }}>{emp.employee_code}</td>
                          <td style={{ ...stickyTd, left: 72, zIndex: 2 }}>{emp.full_name}</td>
                          <td style={{ ...stickyTd, left: 232, zIndex: 2, color: HRC.muted, borderRight: `1px solid ${HRC.border}` }}>
                            {emp.department_name || '—'}
                          </td>
                          {days.map((d) => (
                            <DayCell key={d.day} day={d.day} sunday={sundaySet.has(d.day)}
                              cell={(emp.cells || {})[String(d.day)]}
                              quarantine={quarByDay.get(d.day)}
                              onClick={() => setDetail({ emp, day: d.day, cell: (emp.cells || {})[String(d.day)] })} />
                          ))}
                          {SUM_COLS.map((s) => {
                            let val = Number(sm[s.key] || 0);
                            if (s.key === 'units' && !isPiece) {
                              return <td key={s.key} style={{ textAlign: 'center', fontSize: 11, color: '#C9C9C9', borderBottom: '1px solid #F1F0EE' }}>—</td>;
                            }
                            return (
                              <td key={s.key} style={{ textAlign: 'center', fontSize: 12, fontWeight: 700,
                                color: val ? s.color : '#C9C9C9', borderBottom: '1px solid #F1F0EE', borderLeft: '1px solid #F7F6F3' }}>
                                {val || '·'}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <ListFooter shown={employees.length} total={employees.length} noun="employees" />
          </>
        )}
      </Card>

      {/* Cell detail popover (as a small modal) */}
      {detail && (
        <Modal title={`${detail.emp.full_name} · Day ${detail.day}`} width={360} onClose={() => setDetail(null)}>
          {detail.cell ? (
            <div style={{ fontSize: 13, color: HRC.text }}>
              <div style={{ marginBottom: 12 }}>
                <Pill value={detail.cell.status} />
                <span style={{ fontSize: 12, color: HRC.muted, marginLeft: 10 }}>
                  {detail.emp.employee_code} · {detail.emp.department_name || '—'}
                </span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <tbody>
                  {[
                    ['Late', detail.cell.late > 0 ? `${detail.cell.late} min` : '—'],
                    ['Out early', detail.cell.early > 0 ? `${detail.cell.early} min` : '—'],
                    ['Miss-punch', detail.cell.miss ? 'Yes' : 'No'],
                    ['Overtime', detail.cell.ot > 0 ? `${detail.cell.ot} h` : '—'],
                    ['Units', detail.cell.units > 0 ? detail.cell.units : '—'],
                  ].map(([k, v]) => (
                    <tr key={k}>
                      <td style={{ padding: '6px 0', color: HRC.muted, fontWeight: 600, width: '45%' }}>{k}</td>
                      <td style={{ padding: '6px 0', color: HRC.text, fontWeight: 700 }}>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                {goTo && (
                  <Btn variant="ghost" size="sm" onClick={() => { setDetail(null); goTo('exceptions'); }}>Open exceptions</Btn>
                )}
                <Btn variant="soft" size="sm" onClick={() => setDetail(null)}>Close</Btn>
              </div>
            </div>
          ) : (
            <Empty>No record for this day.</Empty>
          )}
        </Modal>
      )}

      {/* Import history modal */}
      {showLog && (
        <Modal title="Import history" width={640} onClose={() => setShowLog(false)}>
          {logLoading ? (
            <Loading />
          ) : !log || !log.length ? (
            <Empty>No imports recorded yet.</Empty>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    {['When', 'Source', 'Range', 'Rows', 'Status', 'By'].map((h) => (
                      <th key={h} style={{ textAlign: 'left', padding: '6px 8px', fontSize: 10, color: '#888',
                        fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: `1px solid ${HRC.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {log.map((l, i) => (
                    <tr key={i} title={l.message || undefined}>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #F1F0EE', whiteSpace: 'nowrap' }}>{fmtDate(l.created_at)}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #F1F0EE' }}>{l.source || '—'}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #F1F0EE', whiteSpace: 'nowrap' }}>
                        {l.from_date ? `${fmtDate(l.from_date)} → ${fmtDate(l.to_date)}` : '—'}
                      </td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #F1F0EE', fontWeight: 700 }}>{Number(l.rows_imported || 0)}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #F1F0EE' }}><Pill value={l.status} /></td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #F1F0EE', color: HRC.muted }}>{l.run_by_name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
